import { COMMERCE } from '@/config/site';
import { getBySku } from '@/data/products';

import type { CartAction, CartLine, CartState } from './cart.types';

export const initialCartState: CartState = { lines: [], isOpen: false };

/**
 * Mantém a quantidade em 0…max e nunca deixa passar `NaN`.
 *
 * `Math.trunc(NaN)` é `NaN`, `Math.min/max` com `NaN` também é `NaN`, e
 * `NaN < 1` é `false` — sem esta guarda, um valor corrompido no localStorage
 * atravessa a validação e vira "R$ NaN" na mensagem do WhatsApp.
 */
function clampQuantity(quantity: number): number {
  if (!Number.isFinite(quantity)) return 0;
  return Math.min(Math.max(Math.trunc(quantity), 0), COMMERCE.maxQuantityPerItem);
}

/** Só entra no carrinho o que pode ser vendido. */
function isSellable(sku: string): boolean {
  const entry = getBySku(sku);
  return Boolean(entry) && entry!.variant.stock !== 'out-of-stock';
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'add': {
      // Guarda contra SKU inexistente e contra variante esgotada. O botão já
      // aparece desabilitado, mas o reducer é a fronteira que vale.
      if (!isSellable(action.sku)) return state;

      const existing = state.lines.find((line) => line.sku === action.sku);

      if (!existing) {
        const quantity = clampQuantity(action.quantity);
        if (quantity < 1) return state;
        return { ...state, lines: [...state.lines, { sku: action.sku, quantity }] };
      }

      const quantity = clampQuantity(existing.quantity + action.quantity);
      return {
        ...state,
        lines: state.lines.map((line) => (line.sku === action.sku ? { ...line, quantity } : line)),
      };
    }

    case 'setQuantity': {
      const quantity = clampQuantity(action.quantity);

      // Zerar uma linha é o mesmo gesto que removê-la.
      if (quantity < 1) {
        return { ...state, lines: state.lines.filter((line) => line.sku !== action.sku) };
      }

      // Aumentar a quantidade de um item esgotado não faz sentido; diminuir sim.
      const existing = state.lines.find((line) => line.sku === action.sku);
      if (existing && quantity > existing.quantity && !isSellable(action.sku)) return state;

      return {
        ...state,
        lines: state.lines.map((line) => (line.sku === action.sku ? { ...line, quantity } : line)),
      };
    }

    case 'remove':
      return { ...state, lines: state.lines.filter((line) => line.sku !== action.sku) };

    case 'removeUnavailable':
      return { ...state, lines: state.lines.filter((line) => isSellable(line.sku)) };

    case 'clear':
      return { ...state, lines: [] };

    case 'replaceLines':
      return { ...state, lines: action.lines };

    case 'setOpen':
      return { ...state, isOpen: action.isOpen };

    default:
      return state;
  }
}

/**
 * Valida um carrinho lido do localStorage.
 *
 * Qualquer coisa pode estar lá: um valor editado à mão ou um SKU que saiu do
 * catálogo desde a última visita. Entradas inválidas são descartadas em vez de
 * quebrarem a primeira renderização.
 *
 * Itens esgotados são MANTIDOS de propósito — some-los sem avisar confunde
 * ("cadê minha ração?"). A UI marca a linha e oferece removê-la.
 */
export function sanitiseLines(value: unknown): CartLine[] {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const lines: CartLine[] = [];

  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;

    const { sku, quantity } = entry as Partial<CartLine>;
    if (typeof sku !== 'string' || typeof quantity !== 'number') continue;
    if (seen.has(sku) || !getBySku(sku)) continue;

    const safeQuantity = clampQuantity(quantity);
    if (safeQuantity < 1) continue;

    seen.add(sku);
    lines.push({ sku, quantity: safeQuantity });
  }

  return lines;
}

export { clampQuantity };
